import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Plus, Edit, Trash2, Layout,
  FileText, Loader2, Save, X, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { formatDate } from '@utils';
import api from '@services/api';
import type { TicketTemplate, ListResponse } from '@types';
import TicketDesigner from '@components/tickets/TicketDesigner';

const templateSchema = z.object({
  name: z.string().min(2, 'Name is required'),
  description: z.string().optional(),
  width_mm: z.number().min(50),
  height_mm: z.number().min(50),
  page_orientation: z.enum(['portrait', 'landscape']),
  page_size: z.string().default('A4'),
  elements: z.array(z.any()), // Use array for designer elements
  background_image: z.string().nullable().optional(),
  is_active: z.boolean(),
  is_default: z.boolean(),
});

type TemplateForm = z.infer<typeof templateSchema>;

export default function TicketTemplatesPage() {
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TicketTemplate | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const queryClient = useQueryClient();

  const { data: templates, isLoading } = useQuery({
    queryKey: ['ticket-templates', searchQuery, currentPage],
    queryFn: () => api.get<ListResponse<TicketTemplate>>('/ticket-templates', {
      search: searchQuery || undefined,
      page: currentPage,
    }),
  });

  const form = useForm<TemplateForm>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      width_mm: 210,
      height_mm: 297,
      page_orientation: 'portrait',
      page_size: 'A4',
      is_active: true,
      is_default: false,
      elements: [], // Default empty array for designer
      background_image: null,
    }
  });

  const createMutation = useMutation({
    mutationFn: (data: TemplateForm) => api.post('/ticket-templates', data),
    onSuccess: () => {
      toast.success('Template created');
      queryClient.invalidateQueries({ queryKey: ['ticket-templates'] });
      setShowModal(false);
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to create'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: TemplateForm }) =>
      api.put(`/ticket-templates/${id}`, data),
    onSuccess: () => {
      toast.success('Template updated');
      queryClient.invalidateQueries({ queryKey: ['ticket-templates'] });
      setEditingTemplate(null);
      setShowModal(false);
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to update'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/ticket-templates/${id}`),
    onSuccess: () => {
      toast.success('Template deleted');
      queryClient.invalidateQueries({ queryKey: ['ticket-templates'] });
    },
  });

  const handleSubmit = (data: TemplateForm) => {
    if (editingTemplate) {
      updateMutation.mutate({ id: editingTemplate.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (tmpl: TicketTemplate) => {
    setEditingTemplate(tmpl);
    form.reset({
      name: tmpl.name,
      description: tmpl.description || '',
      width_mm: tmpl.widthMm,
      height_mm: tmpl.heightMm,
      page_orientation: tmpl.widthMm > tmpl.heightMm ? 'landscape' : 'portrait',
      page_size: tmpl.pageSize || 'A4',
      is_active: tmpl.isActive,
      is_default: tmpl.isDefault,
      elements: tmpl.elements || [],
      background_image: tmpl.backgroundImage,
    });
    setShowModal(true);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Ticket Templates</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Design and manage PDF ticket layouts</p>
        </div>
        <button onClick={() => { setEditingTemplate(null); form.reset(); setShowModal(true); }} className="btn-primary">
          <Plus className="w-4 h-4" /> Create Template
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          [...Array(3)].map((_, i) => (
            <div key={i} className="card h-64 animate-pulse bg-gray-200 dark:bg-gray-800" />
          ))
        ) : templates?.data?.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            <Layout className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No templates found.</p>
          </div>
        ) : (
          templates?.data?.map((tmpl: any) => (
            <motion.div key={tmpl.id} layout className="card group relative overflow-hidden flex flex-col">
              <div className="aspect-[3/4] bg-gray-100 dark:bg-gray-900 relative">
                {tmpl.backgroundImage || tmpl.previewImage ? (
                  <img src={tmpl.backgroundImage || tmpl.previewImage} alt={tmpl.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="flex items-center justify-center w-full h-full text-gray-400">
                    <FileText className="w-16 h-16 opacity-20" />
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => handleEdit(tmpl)} className="p-2 bg-white rounded shadow hover:bg-gray-50 text-gray-700" aria-label="Edit Template">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button onClick={() => { if (confirm('Delete this template?')) deleteMutation.mutate(tmpl.id); }} className="p-2 bg-white rounded shadow hover:bg-danger-50 text-danger-600" aria-label="Delete Template">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="p-4 flex-1">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{tmpl.name}</h3>
                    <p className="text-xs text-gray-500">{tmpl.description}</p>
                  </div>
                  {tmpl.isDefault && <span className="badge bg-primary-100 text-primary-700 text-[10px]">Default</span>}
                </div>
                <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
                  <span>{tmpl.widthMm}x{tmpl.heightMm}mm</span>
                  <span>Updated {formatDate(tmpl.updatedAt || tmpl.createdAt)}</span>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {templates?.meta && templates.meta.lastPage > 1 && (
        <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-lg">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Showing {(templates.meta.currentPage - 1) * templates.meta.perPage + 1} to{' '}
            {Math.min(templates.meta.currentPage * templates.meta.perPage, templates.meta.total)} of{' '}
            {templates.meta.total} templates
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="btn-secondary p-2"
              aria-label="Previous Page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Page {currentPage} of {templates.meta.lastPage}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(templates.meta.lastPage, p + 1))}
              disabled={currentPage === templates.meta.lastPage}
              className="btn-secondary p-2"
              aria-label="Next Page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="modal-content !max-w-[95vw] !h-[95vh] flex flex-col overflow-hidden bg-gray-50 dark:bg-gray-900" // Full screen modal
            onClick={e => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-white dark:bg-gray-800 z-10 shrink-0">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">{editingTemplate ? 'Edit Template' : 'New Template'}</h3>
              <button onClick={() => setShowModal(false)} aria-label="Close Modal"><X className="w-5 h-5" /></button>
            </div>

            <form onSubmit={form.handleSubmit(handleSubmit)} className="flex-1 flex flex-col overflow-hidden">
              {/* Metadata Bar */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 shrink-0">
                <div>
                  <label className="label text-xs">Name</label>
                  <input {...form.register('name')} className="input text-sm py-1" />
                  {form.formState.errors.name && <p className="text-danger-600 text-xs mt-1">{form.formState.errors.name.message as string}</p>}
                </div>
                <div>
                  <label className="label text-xs">Width (mm)</label>
                  <input type="number" {...form.register('width_mm', { valueAsNumber: true })} className="input text-sm py-1" />
                </div>
                <div>
                  <label className="label text-xs">Height (mm)</label>
                  <input type="number" {...form.register('height_mm', { valueAsNumber: true })} className="input text-sm py-1" />
                </div>
                <div className="flex items-end gap-4 pb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" {...form.register('is_active')} className="rounded text-primary-600" />
                    <span className="text-xs text-gray-700 dark:text-gray-300">Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" {...form.register('is_default')} className="rounded text-primary-600" />
                    <span className="text-xs text-gray-700 dark:text-gray-300">Default</span>
                  </label>
                </div>
              </div>

              {/* Designer Workspace */}
              <div className="flex-1 overflow-hidden bg-gray-200 dark:bg-gray-950 p-4">
                <Controller
                  control={form.control}
                  name="elements"
                  render={({ field }) => (
                    <TicketDesigner
                      widthMm={form.watch('width_mm')}
                      heightMm={form.watch('height_mm')}
                      elements={field.value}
                      onChange={field.onChange}
                      backgroundImage={form.watch('background_image') ?? null}
                      onBackgroundImageChange={(url) => form.setValue('background_image', url)}
                    />
                  )}
                />
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 bg-white dark:bg-gray-800 shrink-0">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Cancel</button>
                <button type="submit" disabled={createMutation.isPending || updateMutation.isPending} className="btn-primary">
                  {(createMutation.isPending || updateMutation.isPending) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Template
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}
